import React, { useEffect, useRef, useState } from 'react'
import SectionWithTag from '../../../../../components/SectionWithTag'
import { FormattedMessage } from 'react-intl'
import SegmentButton from './SegmentButton'
import { twMerge } from 'tailwind-merge'
import Separator from '../../../../../components/Separator'
import ModelWrapper from './Model/ModelWrapper'
import FadeIn from '../../../../../components/Animation/FadeIn'
import { CameraControls } from './Model/CameraControls'
import { PaddingProps } from '../../../../../utils/PaddingContext'

const data = [
    {
        id: 'jumps',
        description: ['precise', 'structure-based', 'optimization'],
        rotation: Math.PI,
    },
    {
        id: 'accuracy',
        description: ['streamlined', 'analyzed', 'merging'],
        rotation: (Math.PI * 4) / 3.5,
    },
    {
        id: 'optimization',
        description: ['properties', 'targeting', 'optimizing'],
        rotation: (Math.PI * 4) / 3,
    },
]

interface SegmentsProps {
  contentMargin: number
  padding?: PaddingProps
}

const timeout = 15000

const Segments = ({ contentMargin, padding }: SegmentsProps) => {
    const [animate, setAnimate] = useState(false)
    const [currentIndex, setCurrentIndex] = useState(0)
    const cameraControls = useRef<CameraControls | null>(null)

    useEffect(() => {
        if (cameraControls.current) {
            const dx = data[currentIndex].rotation - cameraControls.current?.azimuthAngle
      cameraControls.current?.rotate(dx, 0, true)
        }
    }, [currentIndex])
    useEffect(() => {
        let intervalID: NodeJS.Timeout
        if (animate) {
            intervalID = setInterval(
                () => setCurrentIndex(currentIndex < data.length - 1 ? currentIndex + 1 : 0),
                timeout,
            )
        } else {
            setCurrentIndex(0)
        }
        return () => {
            clearInterval(intervalID)
        }
    }, [currentIndex, animate])

    return (
        <FadeIn
            style={{ marginTop: `${contentMargin}px` }}
            scrollableAncestor={window}
            onEnter={() => setAnimate(true)}
            onLeave={() => setAnimate(false)}
        >
            <div className='relative z-10 pb-16'>
                <SectionWithTag tag={{ id: 'section.howItWorks', className: 'hidden md:flex' }}>
                    <div className='max-w-[350px] md:max-w-[620px] mx-auto text-center mb-8'>
                        <FormattedMessage id={'page.technology.enables'} tagName='h2' />
                    </div>
                </SectionWithTag>
                <div className='grid md:grid-cols-3 gap-y-5 md:gap-x-5 side-padding md:content-padding md:w-max'>
                    {data.map((part, index) => (
                        <SegmentButton
                            key={part.id}
                            partId={part.id}
                            currentId={data[currentIndex].id}
                            onClick={() => setCurrentIndex(index)}
                        />
                    ))}
                </div>
                <div className='md:flex content-padding justify-center items-center pt-6 hidden'>
                    {data.map((_, index) => (
                        <>
                            <p className={twMerge('transition', index === currentIndex && 'opacity-100')}>
                                {('0' + (index + 1)).slice(-2)}
                            </p>
                            {index !== data.length - 1 && (
                                <div className='px-3 w-[209px]'>
                                    <Separator />
                                </div>
                            )}
                        </>
                    ))}
                </div>
                <div
                    className='relative md:min-h-[700px] flex items-center flex-col md:flex-row py-12 md:py-0'
                    style={{ ...padding }}
                >
                    <div className='flex flex-col gap-y-10 relative md:w-1/2 md:mr-24 md:max-w-[410px] side-padding md:p-0 text-center md:text-left'>
                        <FormattedMessage id={`page.technology.${data[currentIndex].id}`} tagName='h4' />
                        <FormattedMessage id={`page.technology.${data[currentIndex].id}.description`}>
                            {(value) => <p className='text-[14px] tracking-normal'>{value}</p>}
                        </FormattedMessage>
                    </div>
                    <ModelWrapper cameraControls={cameraControls} />
                </div>
            </div>
        </FadeIn>
    )
}

export default Segments
